import {
  fetchRegionCamerasByStatus,
  fetchRegionFeatured,
  fetchRegionFeaturedCam, fetchRegionMeta, fetchRegionRelated, fetchRegionRelatedNews,
} from 'api/region/static';
import {
  IRegionCam, IRegionFeaturedResort, IRegionRamResponse, IRegionShort,
} from 'api/region/types';
import { IRelatedNews, OpenFlag } from 'api/resort/types';
import { AxiosResponse } from 'axios';
import { BlockContent } from 'components/blocks/BlockContent';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { NextLinkTr } from 'components/blocks/NextLink';
import { FeaturedCamera } from 'components/common/FeaturedCamera';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import { CurrentLink, CurrentTab } from 'components/region/RegionHeaderNav/constants';
import { RegionSmallHeader } from 'components/region/RegionSmallHeader';
import { WeatherReportCameras } from 'components/region/WeatherReportCameras';
import { Main } from 'components/resort/Layout/Main';
import { Side } from 'components/resort/Layout/Side';
import { RegularAd } from 'googlead/components/RegularAd';
import { SidebarAd } from 'googlead/components/SidebarAd';
import { useMustacheTranslation, useOTSTranslation } from 'i18n/useOTSTranslation';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { useRegionLayoutBubble } from 'page-components/region/_context';
import { IRegionLayout } from 'page-components/region/_layout';
import React from 'react';
import { Routes } from 'routes';

import { getRegionSlug } from 'util/memcache';
import { useFakeQueryWithInitialValue } from 'util/useQueryWithInitialValue';
import PlanYourTrip from 'components/common/PlanYourTrip/RegionWidget';
import { isMainLocale } from 'util/i18nFlags';
import { Hero400Error } from 'components/400';
import cameraStyles from 'components/common/FeaturedCamera/styles.module.scss';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';

export type RegionCamsPageProps = {
  cameras: Record<OpenFlag, IRegionRamResponse>,
  featuredCam: IRegionCam,
  numResorts: number,
  featured: IRegionFeaturedResort,
  pathInfo: IRegionShort,
  relatedNews: IRelatedNews | null,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-cams-page';
export const TEMPLATE_ID = '2.1.2';
export const TEMPLATE_NAME = 'Region Cams';

export const REGION_CAM_PAGE = 48;

export default function RegionCamsPage({
  featuredCam, cameras, pathInfo, numResorts,
  relatedNews: preRelatedNews,
}: RegionCamsPageProps) {
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');
  /**
   * Refresh data with new one
   */
  const {
    current: curCameras,
    hasFreshData: areCamerasFresh,
  } = useFakeQueryWithInitialValue(['region-fetchRegionCameras', pathInfo.uuid],
    async () => ({
      [OpenFlag.Open]: await fetchRegionCamerasByStatus(pathInfo.uuid, OpenFlag.Open, 0, REGION_CAM_PAGE),
      [OpenFlag.WeekendsOnly]: await fetchRegionCamerasByStatus(pathInfo.uuid, OpenFlag.WeekendsOnly, 0, REGION_CAM_PAGE),
      [OpenFlag.Closed]: await fetchRegionCamerasByStatus(pathInfo.uuid, OpenFlag.Closed, 0, REGION_CAM_PAGE),
      [OpenFlag.NoReportAvailable]: await fetchRegionCamerasByStatus(pathInfo.uuid, OpenFlag.NoReportAvailable, 0, REGION_CAM_PAGE),
      [OpenFlag.TemporarilyClosed]: await fetchRegionCamerasByStatus(pathInfo.uuid, OpenFlag.TemporarilyClosed, 0, REGION_CAM_PAGE),
      [OpenFlag.PermanentlyClosed]: await fetchRegionCamerasByStatus(pathInfo.uuid, OpenFlag.PermanentlyClosed, 0, REGION_CAM_PAGE),
    }),
    cameras);

  const { current: curFeaturedCam, hasFreshData: areFeaturedFresh } = useFakeQueryWithInitialValue(['region-fetchRegionFeaturedCam', pathInfo.uuid],
    async () => fetchRegionFeaturedCam(pathInfo.uuid),
    featuredCam);

  const { current: relatedNews, hasFreshData: hasFreshRelatedNews } = useFakeQueryWithInitialValue(
    ['region-relatedNews', pathInfo.uuid],
    async () => fetchRegionRelatedNews(pathInfo.uuid),
    preRelatedNews,
  );

  useRegionLayoutBubble({});
  const showLodgingWidget = isMainLocale();

  if (!curCameras) {
    return <Hero400Error light />;
  }

  return (
    <>
      <Main>
        {curFeaturedCam && (
          <>
            <RegionSmallHeader
              title={t('common.featuredCam')}
            >
              <NextLinkTr href={Routes.resortCams(curFeaturedCam.resort)}>{t('common.seeAllCams')}</NextLinkTr>
            </RegionSmallHeader>
            <FeaturedCamera
              href={Routes.resortSnowReport(curFeaturedCam.resort)}
              cam={curFeaturedCam.webcam}
              isFresh={areFeaturedFresh}
              className={cameraStyles.mainView}
              title={curFeaturedCam.resort.title}
            />
          </>
        )}
        <BoxWithShadow>
          <BlockHeader title={t('region.cams.seoTitle', { region: pathInfo.title })}>
            {curFeaturedCam && (
              <NextLinkTr
                href={Routes.regionSnowReport(pathInfo)}
              >
                {t('region.cams.textSnowReport')}
              </NextLinkTr>
            )}
          </BlockHeader>
          <BlockContent>
            {m('region.cams.seoText', {
              region: pathInfo.title,
              numresorts: numResorts,
            })}
          </BlockContent>
        </BoxWithShadow>
        {!!cameras[OpenFlag.Open].pagination.count && (
          <>
            <WeatherReportCameras
              showSnowReport
              regionId={pathInfo.uuid}
              cameras={cameras[OpenFlag.Open]}
              openFlag={OpenFlag.Open}
              isFresh={areCamerasFresh}
            />
            <RegularAd />
          </>
        )}
        {!!cameras[OpenFlag.WeekendsOnly].pagination.count && (
          <>
            <WeatherReportCameras
              showSnowReport
              regionId={pathInfo.uuid}
              cameras={cameras[OpenFlag.WeekendsOnly]}
              openFlag={OpenFlag.WeekendsOnly}
              isFresh={areCamerasFresh}
            />
            <RegularAd />
          </>
        )}
        {!!cameras[OpenFlag.TemporarilyClosed].pagination.count && (
          <>
            <WeatherReportCameras
              showSnowReport
              regionId={pathInfo.uuid}
              cameras={cameras[OpenFlag.TemporarilyClosed]}
              openFlag={OpenFlag.TemporarilyClosed}
              isFresh={areCamerasFresh}
            />
            <RegularAd />
          </>
        )}
        {!!cameras[OpenFlag.NoReportAvailable].pagination.count && (
          <>
            <WeatherReportCameras
              regionId={pathInfo.uuid}
              cameras={cameras[OpenFlag.NoReportAvailable]}
              openFlag={OpenFlag.NoReportAvailable}
              isFresh={areCamerasFresh}
            />
            <RegularAd />
          </>
        )}
        {!!cameras[OpenFlag.Closed].pagination.count && (
          <>
            <WeatherReportCameras
              showSnowReport
              regionId={pathInfo.uuid}
              cameras={cameras[OpenFlag.Closed]}
              openFlag={OpenFlag.Closed}
              isFresh={areCamerasFresh}
            />
            <RegularAd />
          </>
        )}
        {showLodgingWidget && (
          <PlanYourTrip
            hasSkiRental={false}
            region={pathInfo}
            isPartner={false}
          />
        )}
      </Main>
      <Side>
        <SurfaceRelatedArticles
          relatedNewsList={relatedNews}
          isFresh={hasFreshRelatedNews}
        />
        <SidebarAd />
      </Side>
    </>
  );
}

export const fetchProps = async ({ region }:{ region: string }): Promise<RegionCamsPageProps> => {
  const cache: AxiosResponse<IRegionShort> = await assertShouldBeNot404(() => getRegionSlug(region));
  const { uuid } = cache.data;
  const featured = await fetchRegionFeatured(uuid);

  return {
    /**
       * Meta
       */
    type: 'region',
    route: 'cams',

    /**
       * Content
       */
    cameras: {
      [OpenFlag.Open]: await fetchRegionCamerasByStatus(uuid, OpenFlag.Open, 0, REGION_CAM_PAGE),
      [OpenFlag.WeekendsOnly]: await fetchRegionCamerasByStatus(uuid, OpenFlag.WeekendsOnly, 0, REGION_CAM_PAGE),
      [OpenFlag.Closed]: await fetchRegionCamerasByStatus(uuid, OpenFlag.Closed, 0, REGION_CAM_PAGE),
      [OpenFlag.NoReportAvailable]: await fetchRegionCamerasByStatus(uuid, OpenFlag.NoReportAvailable, 0, REGION_CAM_PAGE),
      [OpenFlag.TemporarilyClosed]: await fetchRegionCamerasByStatus(uuid, OpenFlag.TemporarilyClosed, 0, REGION_CAM_PAGE),
      [OpenFlag.PermanentlyClosed]: await fetchRegionCamerasByStatus(uuid, OpenFlag.PermanentlyClosed, 0, REGION_CAM_PAGE),
    },
    featured,
    featuredCam: await fetchRegionFeaturedCam(uuid),
    pathInfo: cache.data,
    numResorts: (await fetchRegionMeta(uuid))?.resorts?.active,
    relatedNews: await fetchRegionRelatedNews(uuid),

    /**
       * Layout
       */
    related: await fetchRegionRelated(uuid),
    navigation: {
      head: HeaderNavCurLink.SNOW_REPORTS,
      tab: CurrentTab.SNOW,
      link: CurrentLink.CAMS,
    },
    adTKey: 'metaregion cams',
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    hero: {
      title: 'region.cams.heroTitle',
    },
    meta: {
      redirectTo: cache.data.domain ?? null,
      description: 'MetaRegion.cams.description',
      title: 'MetaRegion.cams.title',
    },
    prefetch: false,
  };
};

// This function gets called at build time
// eslint-disable-next-line max-len
export const getRegionCamsPageStaticProps = wrapStaticProps(fetchProps);

export const getRegionCamsPageServerSideProps = wrapServerSideProps(fetchProps);
